import { useEffect, useRef, useState } from 'react';
import { Reveal, storeHref, usePlatform } from '../../siteHelpers';
import lessonKeys from '../../assets/lesson-keys.svg';
import dice from '../../assets/dice.png';

// Mirrors the app's real "Natural Notes" drill (NoteSelectorFragment + PianoInstrument):
// no audio and no separate submit. The target note is shown and tapping the matching
// piano key is the answer itself. Fixed order every time: this is a sample teaser.
// A wrong tap works like the app's Back to Basics reveal (NoteSelectorFragment +
// constants/RevealMarks.ts): the tapped key turns red with ✗, the correct key green
// with ✓, and the drill waits for Next. A correct tap moves straight on.
const SEQUENCE = ['F4', 'C4', 'G4'];
const SOLFEGE = { C: 'Do', D: 'Re', E: 'Mi', F: 'Fa', G: 'Sol', A: 'La', B: 'Si' };
const WHITE_KEYS = ['C4', 'D4', 'E4', 'F4', 'G4', 'A4', 'B4'];
const BLACK_KEY_POSITIONS = ['12.7%', '26.4%', '55.3%', '69%', '82.7%'];
const DEFAULT_FEEDBACK = 'Tap the key that matches the note above.';
const NEXT_QUESTION_DELAY = 1400;
const CORRECT_MARK = '✓';
const WRONG_MARK = '✗';

const Teaser = () => {
  const platform = usePlatform();
  const [qIndex, setQIndex] = useState(0);
  const [picked, setPicked] = useState(null);
  const [result, setResult] = useState(null); // 'correct' | 'wrong' | null
  const [hadMistake, setHadMistake] = useState(false);
  const [done, setDone] = useState(false);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const answered = picked !== null;
  const letter = SEQUENCE[qIndex].charAt(0);

  const handleKey = (note) => {
    if (answered) return;
    const correct = note === SEQUENCE[qIndex];
    setPicked(note);
    setResult(correct ? 'correct' : 'wrong');
    if (correct) {
      timer.current = setTimeout(goToNext, NEXT_QUESTION_DELAY);
    } else {
      // Like the app: stay on this question until the user taps Next.
      setHadMistake(true);
    }
  };

  const goToNext = () => {
    if (qIndex + 1 >= SEQUENCE.length) {
      setDone(true);
    } else {
      setQIndex(qIndex + 1);
      setPicked(null);
      setResult(null);
    }
  };

  const isRevealing = result === 'wrong' && !done;

  // Green ✓ on the correct key, red ✗ on a wrongly tapped key.
  const keyState = (note) => {
    if (!answered || done) return null;
    if (note === SEQUENCE[qIndex]) return 'correct';
    if (note === picked) return 'wrong';
    return null;
  };

  let feedback = DEFAULT_FEEDBACK;
  let feedbackKind = '';
  if (done) {
    feedback = hadMistake
      ? 'You are in the right place! Download the app and you will rock it!'
      : 'Great job! Download the app to expand your knowledge.';
  } else if (result === 'correct') {
    feedback = 'Correct answer';
    feedbackKind = ' is-correct';
  } else if (isRevealing) {
    // The app shows no banner here, only the marked keys and Next.
    feedback = '';
  }

  // The app freezes the ring during a reveal instead of colouring it.
  const ringState = result === 'correct' ? ' is-correct' : '';

  return (
    <section className="teaser" id="teaser">
      <div className="teaser-visual" aria-hidden="true">
        <img src={lessonKeys} alt="" width="640" height="640" />
      </div>
      <div className="teaser-visual-dice" aria-hidden="true">
        <img src={dice} alt="" width="300" height="300" />
      </div>
      <div className="ts-container">
        <Reveal as="h2">See it. Tap it. Get it.</Reveal>
        <Reveal as="p" className="teaser-intro">
          A small taste of a real Thriill drill. We show you a note, you tap the matching key.
        </Reveal>

        <Reveal className="teaser-widget" id="teaser-widget">
          <div className="drill-header">
            <h3>Natural Notes</h3>
            <p className="drill-tag">DRILL #1</p>
          </div>

          <div className="mini-piano" id="mini-piano">
            <div className="black-keys" aria-hidden="true">
              {BLACK_KEY_POSITIONS.map((left) => (
                <span key={left} className="key black" style={{ left }} />
              ))}
            </div>
            <div className="white-keys">
              {WHITE_KEYS.map((note) => {
                const state = keyState(note);
                return (
                  <button
                    key={note}
                    className={`key white${state ? ` ${state}` : ''}`}
                    type="button"
                    aria-label={note.charAt(0)}
                    disabled={answered}
                    onClick={() => handleKey(note)}
                  >
                    {state && (
                      <span className="key-mark" aria-hidden="true">
                        {state === 'correct' ? CORRECT_MARK : WRONG_MARK}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="prompt-ring-wrap">
            <svg className="progress-ring" viewBox="0 0 120 120" aria-hidden="true">
              <circle className="progress-ring-track" cx="60" cy="60" r="54" />
              <circle className={`progress-ring-arc${ringState}`} cx="60" cy="60" r="54" />
            </svg>
            <div className={`prompt-ring${ringState}`}>
              <span className="prompt-letter" id="prompt-letter">
                {done ? '✓' : letter}
              </span>
              <span className="prompt-solfege" id="prompt-solfege">
                {done ? '' : SOLFEGE[letter]}
              </span>
            </div>
          </div>

          {isRevealing ? (
            <button className="reveal-next-btn" type="button" onClick={goToNext}>
              Next
            </button>
          ) : (
            <p className={`teaser-feedback${feedbackKind}`} id="teaser-feedback" aria-live="polite">
              {feedback}
            </p>
          )}
          <a
            className="ts-btn ts-btn-primary hero-cta"
            id="teaser1-cta"
            href={storeHref(platform)}
            hidden={!done}
          >
            Get the app
          </a>
          <p className="teaser-progress" id="teaser-progress">
            {Math.min(qIndex + 1, SEQUENCE.length)} / {SEQUENCE.length}
          </p>
        </Reveal>
      </div>
    </section>
  );
};

export default Teaser;
